import React from 'react';
import { Typography, Box, Button, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SearchOff, ArrowBack, Home } from '@mui/icons-material';
import Color from '../components/Color';

const PageNotFound = () => {
    const navigate = useNavigate();

    return (
        <Container maxWidth="md">
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    py: 4,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Animated Search Icon */}
                <Box
                    sx={{
                        position: 'relative',
                        mb: 4,
                        animation: 'bounce 2s ease-in-out infinite',
                        '@keyframes bounce': {
                            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                            '50%': { transform: 'translateY(-15px) rotate(-5deg)' },
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: { xs: 120, sm: 150, md: 180 },
                            height: { xs: 120, sm: 150, md: 180 },
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, #ff6b9d15, #fbbf2430)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, #ff6b9d20, transparent)`,
                                animation: 'ripple 2s ease-in-out infinite',
                            },
                            '@keyframes ripple': {
                                '0%': { transform: 'scale(1)', opacity: 1 },
                                '100%': { transform: 'scale(1.3)', opacity: 0 },
                            },
                        }}
                    >
                        <SearchOff
                            sx={{
                                fontSize: { xs: 60, sm: 80, md: 100 },
                                color: '#ff6b9d',
                                zIndex: 1,
                            }}
                        />
                    </Box>
                </Box>

                {/* Error Code with Animation */}
                <Box
                    sx={{
                        position: 'relative',
                        mb: 2,
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{
                            fontSize: { xs: '5rem', sm: '7rem', md: '9rem' },
                            fontWeight: 800,
                            background: `linear-gradient(135deg, #ff6b9d, ${Color.primary.main}, #fbbf24)`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 0,
                            letterSpacing: '-0.02em',
                            position: 'relative',
                            animation: 'gradient 3s ease infinite',
                            backgroundSize: '200% 200%',
                            '@keyframes gradient': {
                                '0%': { backgroundPosition: '0% 50%' },
                                '50%': { backgroundPosition: '100% 50%' },
                                '100%': { backgroundPosition: '0% 50%' },
                            },
                        }}
                    >
                        404
                    </Typography>
                </Box>

                {/* Title */}
                <Typography
                    variant="h4"
                    sx={{
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                        fontWeight: 700,
                        color: '#2d3748',
                        mb: 2,
                    }}
                >
                    Page Not Found
                </Typography>

                {/* Description */}
                <Typography
                    variant="body1"
                    sx={{
                        fontSize: { xs: '0.95rem', sm: '1.1rem' },
                        color: '#718096',
                        mb: 1,
                        maxWidth: 500,
                        lineHeight: 1.6,
                    }}
                >
                    Oops! The page you're looking for doesn't exist.
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                        color: '#a0aec0',
                        mb: 4,
                        maxWidth: 450,
                    }}
                >
                    It might have been moved or deleted, or you may have mistyped the URL.
                </Typography>

                {/* Action Buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' },
                        width: { xs: '100%', sm: 'auto' },
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(-1)}
                        sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            borderColor: '#ff6b9d',
                            color: '#ff6b9d',
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: '#ff6b9d',
                                backgroundColor: '#ff6b9d10',
                            },
                        }}
                    >
                        Go Back
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Home />}
                        onClick={() => navigate('/dashboard')}
                        sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, #ff6b9d, ${Color.primary.main})`,
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            boxShadow: '0 4px 14px #ff6b9d40',
                            '&:hover': {
                                background: `linear-gradient(135deg, #ff5588, #6a0ab0)`,
                                boxShadow: '0 6px 20px #ff6b9d60',
                            },
                        }}
                    >
                        Go to Dashboard
                    </Button>
                </Box>

                {/* Floating Decorative Elements */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '5%',
                        right: '5%',
                        width: { xs: 80, md: 120 },
                        height: { xs: 80, md: 120 },
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, #ff6b9d20, transparent)`,
                        filter: 'blur(50px)',
                        animation: 'float 6s ease-in-out infinite',
                        '@keyframes float': {
                            '0%, 100%': { transform: 'translate(0, 0)' },
                            '33%': { transform: 'translate(30px, -30px)' },
                            '66%': { transform: 'translate(-20px, 20px)' },
                        },
                        zIndex: -1,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '10%',
                        left: '10%',
                        width: { xs: 100, md: 150 },
                        height: { xs: 100, md: 150 },
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${Color.primary.main}20, transparent)`,
                        filter: 'blur(60px)',
                        animation: 'float 7s ease-in-out infinite',
                        animationDelay: '2s',
                        zIndex: -1,
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '5%',
                        width: { xs: 60, md: 90 },
                        height: { xs: 60, md: 90 },
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, #fbbf2420, transparent)`,
                        filter: 'blur(40px)',
                        animation: 'float 5s ease-in-out infinite',
                        animationDelay: '1s',
                        zIndex: -1,
                    }}
                />
            </Box>
        </Container>
    );
};

export default PageNotFound;
